import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { type GetCurrentEmployeeQuery, useGetCurrentEmployeeQuery } from '@/common/graphql';
import { useAuthContext } from './AuthContext';
import { CurrentEmployeeContext, type CurrentEmployeeContextType } from './CurrentEmployeeContext';

/**
 * Must be mounted above `ModalsProvider` in `App.tsx`, not inside it. `ModalsProvider` renders
 * queued modal content as a sibling of its own `children` prop rather than in-place at the
 * `openModal()` call site, so any context established only inside `ModalsProvider`'s `children`
 * (e.g. the previous `RouterWrapper`-scoped `CurrentEmployeeContext.Provider`) is invisible to
 * modal content, no matter how deeply nested the `openModal()` call site is.
 */
export const CurrentEmployeeProvider = ({ children }: { children: ReactNode }) => {
  const { authUser } = useAuthContext();
  const [currentEmployee, setCurrentEmployee] = useState<GetCurrentEmployeeQuery['currentEmployee'] | null>(null);

  const { loading: currentEmployeeLoading, data } = useGetCurrentEmployeeQuery({
    skip: !!currentEmployee || authUser.email === '',
  });

  useEffect(() => {
    if (data) setCurrentEmployee(data.currentEmployee);
  }, [data]);

  const currentEmployeeContext: CurrentEmployeeContextType = useMemo(
    () => ({ loading: currentEmployeeLoading, currentEmployee, setCurrentEmployee }),
    [currentEmployee, currentEmployeeLoading],
  );

  return <CurrentEmployeeContext.Provider value={currentEmployeeContext}>{children}</CurrentEmployeeContext.Provider>;
};
